import * as T from 'three';

// One instanced draw per scene, no textures and no per-spinner materials. The
// shader rotates a soft ring in screen space; CPU work only packs visible centers.
export class LoadingSpinners {
    constructor(roots) {
        this.entries = new Set();
        this.material = new T.ShaderMaterial({
            transparent: true, depthWrite: false, toneMapped: false,
            uniforms: { time: { value: 0 }, viewport: { value: new T.Vector2(1, 1) },
                tint: { value: new T.Color(0xa8d8ef) } },
            vertexShader: `
                attribute vec3 center;
                uniform vec2 viewport;
                varying vec2 ringPosition;
                void main() {
                    ringPosition = position.xy;
                    vec4 clip = projectionMatrix * viewMatrix * vec4(center, 1.0);
                    // 24 CSS pixels across, including in half-resolution water captures.
                    clip.xy += position.xy * 24.0 / viewport * clip.w;
                    gl_Position = clip;
                }`,
            fragmentShader: `
                uniform float time;
                uniform vec3 tint;
                varying vec2 ringPosition;
                void main() {
                    float radius = length(ringPosition);
                    float edge = max(fwidth(radius), 0.001);
                    float ring = smoothstep(0.55 - edge, 0.55 + edge, radius)
                        * (1.0 - smoothstep(0.86 - edge, 0.86 + edge, radius));
                    float turn = fract(atan(ringPosition.y, ringPosition.x) / 6.2831853 + time * 0.85);
                    float alpha = ring * (0.16 + 0.84 * turn * turn);
                    if (alpha < 0.005) discard;
                    gl_FragColor = vec4(tint, alpha);
                    #include <colorspace_fragment>
                }`,
        });
        this.batches = roots.map(root => {
            const geometry = new T.InstancedBufferGeometry();
            geometry.setAttribute('position', new T.Float32BufferAttribute([-1,-1,0, 1,-1,0, 1,1,0, -1,1,0], 3));
            geometry.setIndex([0,1,2, 0,2,3]); geometry.instanceCount = 0;
            const mesh = new T.Mesh(geometry, this.material);
            mesh.name = 'Loading asset spinners'; mesh.frustumCulled = false; mesh.visible = false;
            mesh.matrixAutoUpdate = false;
            mesh.onBeforeRender = renderer => renderer.getSize(this.material.uniforms.viewport.value);
            return { root, mesh, geometry, capacity: 0, count: 0, firstChanged: Infinity, lastChanged: -1 };
        });
        this.byRoot = new Map(this.batches.map(batch => [batch.root, batch]));
    }
    add(parent) {
        const anchor = new T.Object3D();
        // Retain conservative PVS membership while the real model bounds are unknown.
        anchor.boundingSphere = new T.Sphere(new T.Vector3(), 20);
        parent.add(anchor); this.entries.add(anchor);
        return anchor;
    }
    remove(anchor) {
        if (!anchor) return;
        this.entries.delete(anchor); anchor.removeFromParent();
        if (!this.entries.size) for (const batch of this.batches) {
            batch.mesh.removeFromParent(); batch.mesh.visible = false; batch.geometry.instanceCount = 0;
        }
    }
    update(seconds, camera) {
        this.material.uniforms.time.value = seconds % 1000;
        for (const batch of this.batches) {
            // Reserve only on membership growth, never every animation frame.
            if (this.entries.size > batch.capacity) {
                batch.capacity = Math.max(32, 2 ** Math.ceil(Math.log2(this.entries.size)));
                batch.geometry.dispose();
                batch.centers = new T.InstancedBufferAttribute(new Float32Array(batch.capacity * 3), 3).setUsage(T.DynamicDrawUsage);
                batch.geometry.setAttribute('center', batch.centers);
                batch.fresh = true;
            }
            batch.count = 0; batch.firstChanged = Infinity; batch.lastChanged = -1;
        }
        for (const anchor of this.entries) {
            if (camera && !anchor.layers.test(camera.layers)) continue;
            let root = anchor, visible = true, batch;
            while (root) {
                if (!root.visible) { visible = false; break; }
                batch = this.byRoot.get(root);
                if (batch) break;
                root = root.parent;
            }
            if (!visible || !batch || batch.root.parent?.visible === false) continue;
            const at = batch.count++ * 3, matrix = anchor.matrixWorld.elements, values = batch.centers.array;
            for (let axis = 0; axis < 3; axis++) {
                const value = Math.fround(matrix[12 + axis]);
                if (batch.fresh || values[at + axis] !== value) {
                    values[at + axis] = value;
                    batch.firstChanged = Math.min(batch.firstChanged, at + axis);
                    batch.lastChanged = at + axis;
                }
            }
        }
        for (const batch of this.batches) {
            batch.geometry.instanceCount = batch.count; batch.mesh.visible = batch.count > 0;
            if (batch.count) { if (!batch.mesh.parent) batch.root.add(batch.mesh); }
            else batch.mesh.removeFromParent();
            if (batch.lastChanged >= 0) {
                // Coalesce pending edits if no draw consumed the previous upload.
                // Keep this bounded even when a camera skips this scene for ages.
                for (const range of batch.centers.updateRanges) {
                    batch.firstChanged = Math.min(batch.firstChanged, range.start);
                    batch.lastChanged = Math.max(batch.lastChanged, range.start + range.count - 1);
                }
                batch.centers.clearUpdateRanges();
                batch.centers.addUpdateRange(batch.firstChanged, batch.lastChanged - batch.firstChanged + 1);
                batch.centers.needsUpdate = true;
            }
            batch.fresh = false;
        }
    }
    dispose() {
        for (const anchor of this.entries) anchor.removeFromParent();
        this.entries.clear();
        for (const batch of this.batches) { batch.mesh.removeFromParent(); batch.geometry.dispose(); }
        this.material.dispose();
    }
}
